import {
  chipButtonClassName,
  chipDangerButtonClassName,
  chipDeepButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import {
  EYEBROW,
  FOCUS_RING,
  HOVER_T,
  ROW_HOVER,
} from "@/components/rail-kit/registers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils";
import { trackSettingsScopeViewed } from "@/utils/analytics";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { X, type LucideIcon } from "lucide-react";
import React from "react";

/**
 * Manage modal — the shell, ported from the design system
 * (`showcase/lib/manage-modal.jsx`, spec sheet `specs/manage-modal`). One large
 * Dialog whose anatomy is the file browser's scaled up one register: a
 * surface-tab head (the modal's title row), a grouped scope rail on the left
 * (mono section heads + iconed rows + a quick-action footer, the sidebar's
 * grammar), the pane content on the right. The shell owns that anatomy and its
 * contracts (roving-tabindex tablist, entry aliases, landing scopes, the
 * below-md lie-down); consumers own the config and the pane content.
 *
 * A scope may be a page swap OR an in-pane anchor — the shell doesn't care.
 * `setScope` is handed to panes so a pane that stacks scopes can run the rail
 * as its table of contents.
 *
 * Row/hover/focus/eyebrow treatments all come from `rail-kit/registers` (the
 * repo's port of the design system's register vocabulary) — never hand-rolled
 * here, so the shell can't drift off the ladder.
 */

export type ManageRailItem = {
  /** Doubles as the scope key and the row's visible label. */
  id: string;
  icon: LucideIcon;
};

export type ManageRailSection = {
  head?: string;
  items: ManageRailItem[];
};

export type ManageRailAction = {
  icon: LucideIcon;
  label: string;
  tint: "neutral" | "deep" | "danger";
  /** Navigates the rail to this scope. */
  scope?: string;
  /** Real verb. Runs after the scope hop when both are set. */
  onSelect?: () => void;
};

export type ManageRail = {
  sections: ManageRailSection[];
  /** Optional: a tab whose verbs all belong to its rows declares none. */
  actions?: ManageRailAction[];
};

export type ManageTab = { id: string; label: string };

const CHIP_CLASS = {
  neutral: chipButtonClassName,
  deep: chipDeepButtonClassName,
  danger: chipDangerButtonClassName,
} as const;

/**
 * The rail: the browser's row register (min-h-9, n-10 active fill) with the
 * sidebar's grammar — mono section heads, muted leading icons, the
 * quick-action footer pinned to the foot. Below md it lies down into a
 * horizontal strip (heads + footer step aside).
 */
export function ManageModalRail({
  rail,
  active,
  onPick,
}: {
  rail: ManageRail;
  active: string;
  onPick: (scope: string) => void;
}) {
  return (
    <nav
      className="flex w-full shrink-0 flex-row gap-0.5 overflow-x-auto md:w-56 md:flex-col md:overflow-x-visible md:overflow-y-auto md:pr-1"
      aria-label="Sections"
    >
      {rail.sections.map((section, i) => (
        <React.Fragment key={section.head ?? i}>
          {section.head && (
            <div
              className={cn(
                "hidden shrink-0 px-2.5 pb-1 md:block",
                EYEBROW,
                i > 0 && "md:mt-4",
              )}
            >
              {section.head}
            </div>
          )}
          {section.items.map(({ id, icon: Icon }) => {
            const on = id === active;
            return (
              <button
                key={id}
                type="button"
                onClick={() => onPick(id)}
                aria-current={on ? "true" : undefined}
                className={cn(
                  "flex min-h-9 shrink-0 cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-md px-2.5 text-left text-base",
                  FOCUS_RING,
                  HOVER_T,
                  on
                    ? // The modal's ground is n-00 light / n-10 dark, so the
                      // active fill has to step up from whichever it is —
                      // bg-n-10 alone disappears against the dark ground.
                      "bg-n-10 text-n-95 dark:bg-n-20"
                    : cn("text-n-70 hover:text-n-90", ROW_HOVER),
                )}
              >
                <Icon
                  className={cn(
                    "size-3.5 shrink-0",
                    on ? "text-n-70" : "text-n-50",
                  )}
                />
                {id}
              </button>
            );
          })}
        </React.Fragment>
      ))}
      {/* Quick actions — the sidebar's action-chip register, stacked. Optional:
          a tab whose verbs all belong to its rows (Archive) declares none, and
          the block doesn't render rather than reserving its pad. */}
      {rail.actions && rail.actions.length > 0 && (
        <div className="mt-auto hidden shrink-0 flex-col gap-1.5 pt-4 md:flex">
          {rail.actions.map((action) => (
            <Button
              key={action.label}
              variant="ghost"
              size="sm"
              onClick={() => {
                if (action.scope) onPick(action.scope);
                action.onSelect?.();
              }}
              className={cn(
                CHIP_CLASS[action.tint],
                // Sizing after the tint, and on the rail ROW's rung rather
                // than the chip's own: these sit in the same 224px column as
                // the scope rows directly above them, so a shorter box with a
                // tighter radius and a 2px-deeper text inset read as a ragged
                // column rather than as a different register. Same height,
                // radius, inset and type as a row — the chip fill is what
                // marks it as a verb.
                "h-9 min-h-9 w-full cursor-pointer rounded-md px-2.5 py-1.5 text-base focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-3.5",
                FOCUS_RING,
              )}
            >
              <action.icon />
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </nav>
  );
}

/**
 * The anatomy without the Dialog: head + rail + pane. `entry` is the raw tab
 * value (aliases welcome); scope state lives here and re-lands per entry.
 *
 * The head rides Radix Tabs directly — roving tabindex, arrow keys (activation
 * follows focus), Home/End all come from the primitive, never hand-rolled —
 * themed to the surface-tab register: text-md medium, color-only states, no
 * underline track (this shell's zones separate with space).
 */
export function ManageModalBody({
  tabs,
  rails,
  aliases = {},
  landing = {},
  entry,
  onTab,
  renderPane,
  ariaLabel = "Manage",
}: {
  tabs: ManageTab[];
  rails: Record<string, ManageRail>;
  aliases?: Record<string, string>;
  landing?: Record<string, string>;
  entry: string | null;
  onTab: (tab: string) => void;
  renderPane: (
    tabId: string,
    scope: string,
    setScope: (scope: string) => void,
  ) => React.ReactNode;
  ariaLabel?: string;
}) {
  const firstTabId = tabs[0].id;
  // An entry is either a tab id or an alias for one (a deep-link path).
  const tabId: string =
    (entry ? aliases[entry] : undefined) ?? entry ?? firstTabId;
  const rail: ManageRail = rails[tabId] ?? rails[firstTabId];
  const land = React.useCallback(
    (e: string | null) =>
      (e && landing[e]) || rail.sections[0]?.items[0]?.id || "",
    // `landing` and `rail` are rebuilt each render by the config hook; keying
    // the effect below on `entry` alone is the contract (land once per entry).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entry],
  );
  const [scope, setScope] = React.useState(() => land(entry));

  // Landing scope follows the entry point per `landing` (e.g. the gear and the
  // identity line → Settings·Profile, the meter → Usage·Overview).
  React.useEffect(() => {
    if (entry) setScope(land(entry));
  }, [entry, land]);

  // A scope that vanishes (permissions resolve late, a tab swap) falls back to
  // the rail's first row rather than rendering an empty pane.
  const scopeExists = rail.sections.some((s) =>
    s.items.some((i) => i.id === scope),
  );
  const activeScope = scopeExists
    ? scope
    : (rail.sections[0]?.items[0]?.id ?? "");

  // Last-pair ref: StrictMode double effects and scope re-lands must not
  // re-emit an unchanged view.
  const lastViewedRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const pair = `${tabId}:${activeScope}`;
    if (lastViewedRef.current === pair) return;
    lastViewedRef.current = pair;
    trackSettingsScopeViewed({ tab: tabId, scope: activeScope });
  }, [tabId, activeScope]);

  return (
    <TabsPrimitive.Root
      value={tabId}
      onValueChange={onTab}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The head — the surface-tab register doubling as the modal's title row.
          pr-14 below md keeps the last tab clear of the Dialog's close glyph. */}
      <TabsPrimitive.List
        aria-label={ariaLabel}
        className="flex shrink-0 items-center justify-start gap-5 overflow-x-auto pb-3 pl-6 pr-14 pt-5 md:pr-6"
      >
        {tabs.map(({ id, label }) => (
          <TabsPrimitive.Trigger
            key={id}
            value={id}
            className={cn(
              "inline-flex min-h-9 cursor-pointer items-center whitespace-nowrap bg-transparent px-1 text-md font-medium text-n-50 outline-none hover:text-n-90 data-[state=active]:text-n-90",
              "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust focus-visible:outline-offset-[2px]",
              HOVER_T,
            )}
          >
            {label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      {/* The two-column body — scope rail + content, the browser's anatomy. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 pb-6 md:flex-row">
        <ManageModalRail rail={rail} active={activeScope} onPick={setScope} />
        <TabsPrimitive.Content
          value={tabId}
          className={cn(
            // The focus ring sits 2px outside its control, so a field at the
            // very top of the pane had its ring sheared off by this scroller.
            // The negative margin buys that headroom back without moving the
            // content off its shared baseline with the rail.
            "-mt-1 min-h-0 min-w-0 flex-1 overflow-y-auto pr-2 pt-1",
            FOCUS_RING,
          )}
        >
          {renderPane(tabId, activeScope, setScope)}
        </TabsPrimitive.Content>
      </div>
    </TabsPrimitive.Root>
  );
}

/**
 * The product form: the anatomy inside one large Dialog. `tab` doubles as the
 * open state — null closes, any tab or alias value opens landed there.
 */
export function ManageModal({
  tabs,
  rails,
  aliases,
  landing,
  tab,
  onTab,
  onClose,
  renderPane,
  title = "Manage",
}: {
  tabs: ManageTab[];
  rails: Record<string, ManageRail>;
  aliases?: Record<string, string>;
  landing?: Record<string, string>;
  tab: string | null;
  onTab: (tab: string) => void;
  onClose: () => void;
  renderPane: (
    tabId: string,
    scope: string,
    setScope: (scope: string) => void,
  ) => React.ReactNode;
  title?: string;
}) {
  const open = !!tab;

  if (tabs.length === 0) return null;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) onClose();
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          hideCloseButton
          overlayClassName="z-50 bg-[color-mix(in_oklab,var(--ink)_45%,transparent)] backdrop-blur-[2px] duration-150"
          // Focus lands on the frame, not the selected tab — rings are
          // keyboard-only, and a mouse-driven open shouldn't paint one.
          onOpenAutoFocus={(event) => event.preventDefault()}
          // `!` beats the base DialogContent's `sm:max-w-lg` / `max-h-[90vh]`
          // defaults, which otherwise survive the merge and squish the frame.
          className="flex !h-[min(44rem,88vh)] !max-h-none w-[min(58rem,92vw)] max-w-none flex-col gap-0 overflow-hidden rounded-xl border-n-10 bg-popover p-0 shadow-[var(--elev-5)] duration-150 data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100 sm:max-w-none"
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {/* The comp's close glyph: a 28px hit target that acknowledges with
              ground and color, not opacity. */}
          <DialogClose className="absolute right-4 top-4 z-10 inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-n-50 outline-none transition-colors duration-150 ease-out hover:bg-n-10 hover:text-n-90 hover:duration-100 dark:hover:bg-n-20 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-rust">
            <X className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
          <ManageModalBody
            tabs={tabs}
            rails={rails}
            aliases={aliases}
            landing={landing}
            entry={tab}
            onTab={onTab}
            renderPane={renderPane}
            ariaLabel={title}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
